
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.productList = document.querySelector(`ul[data-id="${this.sectionId}"]`);
      this.loadMoreButton = this.querySelector('.btn--load-more');
      this.progressMessage = this.querySelector('[data-pagination-progress-message]');
      this.progressFill = this.querySelector('[data-pagination-progress-fill]');
      this.progressTrack = this.querySelector('[data-pagination-progress-track]');
      this.paginationType = this.getAttribute('data-type');
      this.countItem = parseInt(this.productList?.getAttribute('data-products-count'), 10) || 0;
      this.currentPage = 2;
      this.scrollObserver = null;
    }

    connectedCallback() {
      if (!this.dataset.nextUrl) {
        this.finalizePagination();
        return;
      }

      this.paginationType === 'load-more-button'
        ? this.setupLoadMoreButton()
        : this.paginationType === 'infinite-scroll'
        ? this.setupInfiniteScroll()
        : null;

      this.restoreScrollState();
      this.bindProductLinks();
    }

    bindProductLinks() {
      this.productList?.querySelectorAll('.grid__item a').forEach(link => {
        link.addEventListener('click', () => {
          sessionStorage.setItem('pagination-scrollY', window.scrollY);
          sessionStorage.setItem('pagination-currentPage', this.currentPage - 1);
        });
      });
    }

    setupLoadMoreButton() {
      this.loadMoreButton?.addEventListener('click', () => this.loadNextPage());
    }

    setupInfiniteScroll() {
      this.scrollObserver = new IntersectionObserver(
        entries => entries[0].isIntersecting && this.loadNextPage(),
        { threshold: [0, 1] }
      );
      this.scrollObserver.observe(this);
    }

    async loadNextPage() {
      if (this.hasAttribute('loading')) return;

      if (!this.dataset.nextUrl) {
        this.finalizePagination();
        return;
      }

      try {
        this.setAttribute('loading', true);
        this.toggleLoadingSpinner(true);

        const response = await fetch(this.dataset.nextUrl);

        if (!response.ok) {
          throw new Error(`Failed to load products: ${response.status}`);
        }

        const html = await response.text();

        this.renderFetchedProducts(html);
        this.currentPage++;
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        this.toggleLoadingSpinner(false);
        this.removeAttribute('loading');
      }
    }

    renderFetchedProducts(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newProductGrid = doc.getElementById('product-grid');
      const nextPagination = doc.querySelector(`custom-pagination[data-section="${this.sectionId}"]`);

      if (!newProductGrid?.children.length) {
        this.finalizePagination();
        return;
      }

      newProductGrid.querySelectorAll('.grid__item')
        .forEach(product => this.productList.appendChild(product));

      if (nextPagination?.dataset.nextUrl) {
        this.dataset.nextUrl = nextPagination.dataset.nextUrl;
      } else {
        this.removeAttribute('data-next-url');
        this.finalizePagination();
      }

      this.updateProductCountRange();
      this.updateProgress();
      this.bindProductLinks();
    }

    getVisibleProductCount() {
      return this.productList?.querySelectorAll('.grid__item').length ?? 0;
    }

    updateProgress() {
      const visibleCount = this.getVisibleProductCount();
      const total = parseInt(this.dataset.progressTotal, 10) || this.countItem;
      const percent = total > 0 ? Math.min(100, (visibleCount / total) * 100) : 0;
      const template = this.dataset.progressTemplate || '';

      if (this.progressMessage && template) {
        this.progressMessage.textContent = template
          .replace('__COUNT__', visibleCount)
          .replace('__TOTAL__', total);
      }

      if (this.progressFill) {
        this.progressFill.style.width = `${percent}%`;
      }

      if (this.progressTrack) {
        this.progressTrack.setAttribute('aria-valuenow', String(visibleCount));
        this.progressTrack.setAttribute('aria-valuemax', String(total));

        if (this.progressMessage) {
          this.progressTrack.setAttribute('aria-label', this.progressMessage.textContent);
        }
      }
    }

    updateProductCountRange() {
      const visibleCount = this.getVisibleProductCount();

      document.querySelectorAll('[data-product-count-range][data-count-mode="cumulative"]').forEach((element) => {
        const total = parseInt(element.dataset.rangeTotal, 10) || this.countItem;
        let text;

        if (total === 1) {
          text = (element.dataset.countSingleTemplate || '').replace('__TOTAL__', '1');
        } else {
          text = (element.dataset.countTemplate || '')
            .replace('__START__', '1')
            .replace('__END__', String(visibleCount))
            .replace('__TOTAL__', String(total));
        }

        element.textContent = text;
        element.dataset.rangeEnd = String(visibleCount);
      });
    }

    toggleLoadingSpinner(isLoading) {
      if (!this.loadMoreButton) return;

      const spinner = this.loadMoreButton.querySelector('.loading__spinner');
      this.loadMoreButton.classList.toggle('loading', isLoading);
      spinner?.classList.toggle('hidden', !isLoading);
    }

    finalizePagination() {
      this.scrollObserver?.unobserve(this);
      this.scrollObserver?.disconnect();
      this.classList.add('visually-hidden');
    }

    disconnectedCallback() {
      this.scrollObserver?.disconnect();
      this.loadMoreButton?.removeEventListener('click', this.loadNextPage);
    }

    async restoreScrollState() {
      const savedPage = parseInt(sessionStorage.getItem('pagination-currentPage'), 10);
      const savedScrollY = parseInt(sessionStorage.getItem('pagination-scrollY'), 10);

      if (!isNaN(savedPage) && savedPage >= 2) {
        await this.loadPagesUpTo(savedPage);

        window.setTimeout(() => {
          window.scrollTo(0, savedScrollY);
        }, 100);
        sessionStorage.removeItem('pagination-currentPage');
        sessionStorage.removeItem('pagination-scrollY');
      }

      this.updateProgress();
    }

    async loadPagesUpTo(page) {
      while (this.currentPage <= page) {
        await this.loadNextPage();
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
